import React, { useState, useEffect } from 'react'
import './InventoryManagement.css'
import ImageUpload from './ImageUpload'
import MultipleImageUpload from './MultipleImageUpload'
import { getAllTrailerModels, searchTrailerModels, getTrailersBySourceSheet, TrailerModel } from '../data/trailerModels'

interface TrailerInventoryItem {
  id: number
  model_name: string
  category_id: number
  category_name: string
  type: string
  material: string
  axle_configuration: string
  capacity_pounds: number
  length_feet: number
  beam_width_inches: number
  price: number
  stock_quantity: number
  is_available: boolean
  image_url: string
  image_urls: string[]
  description: string
  features: string[]
  specifications: Record<string, string>
  boat_length: string
  notes: string
  created_at: string
  updated_at: string
}

interface TrailerCategory {
  id: number
  name: string
  description: string
}

// Using the TrailerModel interface from trailerModels.ts instead of VentureModel

interface InventoryManagementProps {
  onClose: () => void
}

const InventoryManagement: React.FC<InventoryManagementProps> = ({ onClose }) => {
  const [inventory, setInventory] = useState<TrailerInventoryItem[]>([])
  const [categories, setCategories] = useState<TrailerCategory[]>([])
  const [trailerModels, setTrailerModels] = useState<TrailerModel[]>([])
  const [filteredTrailerModels, setFilteredTrailerModels] = useState<TrailerModel[]>([])
  const [modelSearchTerm, setModelSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingItemId, setEditingItemId] = useState<number | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  const [formData, setFormData] = useState({
    trailer_model_name: '',
    model_name: '',
    category_id: '',
    type: '',
    material: '',
    axle_configuration: '',
    capacity_pounds: '',
    length_feet: '',
    beam_width_inches: '',
    price: '',
    stock_quantity: '',
    image_file: null as File | null,
    image_files: [] as File[],
    existing_image_urls: [] as string[],
    all_images: [] as (File | string)[],
    description: '',
    boat_length: '',
    notes: '',
    // Individual specification fields
    tongue_weight: '',
    overall_length: '',
    tire_size: '',
    hitch_size: '',
    brake_type: '',
    suspension: '',
    frame: '',
    hull_protection: '',
    guides: '',
    winch: '',
    jack: '',
    wiring: '',
    lights: '',
    fenders: '',
    finish: '',
    // Features as checkboxes
    selectedFeatures: [] as string[]
  })

  // Available feature options
  const availableFeatures = [
    'LED lights',
    'Carpeted bunks',
    'Galvanized hardware',
    'Swivel tongue jack',
    'Electric brakes',
    'Disc brakes',
    'Surge brakes',
    'Corrosion resistant',
    'Self-centering rollers',
    'Adjustable bunks',
    'Lightweight aluminum',
    'Wide beam design',
    'Pontoon guides',
    'Three-pontoon design',
    'Torsion axles',
    'Spare tire bracket',
    'Windlass bow stop',
    'Target bunks',
    'PVC load guides'
  ]

  useEffect(() => {
    fetchInventoryData()
    // Load trailer models from our CSV database
    const models = getAllTrailerModels()
    setTrailerModels(models)
    setFilteredTrailerModels(models)

    // Listen for inventory update events from other components
    const handleInventoryUpdate = (event: CustomEvent) => {
      console.log('InventoryManagement received inventory update event:', event.detail)
      fetchInventoryData()
    }

    window.addEventListener('inventory-updated', handleInventoryUpdate as EventListener)

    // Cleanup
    return () => {
      window.removeEventListener('inventory-updated', handleInventoryUpdate as EventListener)
    }
  }, [])

  // Filter trailer models based on search term
  useEffect(() => {
    if (modelSearchTerm.trim() === '') {
      setFilteredTrailerModels(trailerModels)
    } else {
      const filtered = searchTrailerModels(modelSearchTerm)
      setFilteredTrailerModels(filtered)
    }
  }, [modelSearchTerm, trailerModels])

  const fetchInventoryData = async () => {
    try {
      setLoading(true)
      
      // Fetch inventory and categories from API (trailer models are loaded from our CSV database)
      const [inventoryResponse, categoriesResponse] = await Promise.all([
        fetch('/api/inventory'),
        fetch('/api/categories')
      ])
      
      if (!inventoryResponse.ok) {
        throw new Error(`Failed to fetch inventory: ${inventoryResponse.statusText}`)
      }
      if (!categoriesResponse.ok) {
        throw new Error(`Failed to fetch categories: ${categoriesResponse.statusText}`)
      }
      
      const inventoryData = await inventoryResponse.json()
      const categoriesData = await categoriesResponse.json()
      
      setInventory(inventoryData)
      setCategories(categoriesData)
    } catch (err) {
      setError('Failed to fetch inventory data')
      console.error('Error fetching inventory:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    
    // Handle Trailer model selection
    if (name === 'trailer_model_name' && value) {
      const selectedModel = trailerModels.find(model => model.model === value)
      if (selectedModel) {
        // Map source sheet to category_id
        const categoryMap: { [key: string]: number } = {
          'Aluminum Roller': 1,
          'Galvanized Roller': 2,
          'Pontoon': 3,
          'PWC': 5,
          'Aluminum PWC': 5,
          'Aluminum Bunk': 6,
          'Galvanized Bunk': 6,
          'Commander': 7,
          'Skiff': 9
        }
        
        const categoryId = categoryMap[selectedModel.sourceSheet] || 9
        
        // Parse capacity and dimensions from the model data
        const capacityNum = selectedModel.capacity || 0

        // Handle trailer length - extract from overallLength field like "20'3""
        let lengthNum = parseFloat(selectedModel.overallLength.replace(/[^0-9.]/g, '')) || 0
        
        const beamNum = parseFloat(selectedModel.betweenFenders?.replace(/[^0-9.]/g, '') || selectedModel.overallWidth?.replace(/[^0-9.]/g, '') || '0') || 0
        
        // Determine material based on source sheet
        let material = 'Steel'
        if (selectedModel.sourceSheet.includes('Aluminum') || selectedModel.sourceSheet === 'Commander') {
          material = 'Aluminum'
        } else if (selectedModel.sourceSheet.includes('Galvanized')) {
          material = 'Galvanized Steel'
        }
        
        // Determine axle configuration
        let axleConfig = 'Single'
        if (selectedModel.axles === 2) axleConfig = 'Tandem'
        if (selectedModel.axles === 3) axleConfig = 'Triple'
        
        // Determine type from source sheet
        let type = 'Other'
        if (selectedModel.sourceSheet.includes('Bunk')) type = 'Bunk'
        else if (selectedModel.sourceSheet.includes('Roller')) type = 'Roller'
        else if (selectedModel.sourceSheet.includes('PWC')) type = 'PWC'
        else if (selectedModel.sourceSheet.includes('Pontoon')) type = 'Pontoon'
        else if (selectedModel.sourceSheet === 'Commander') type = 'Bunk'
        else if (selectedModel.sourceSheet === 'Skiff') type = 'Skiff'
        
        // Generate comprehensive features list from model data
        const features: string[] = []
        if (material === 'Aluminum') features.push('Lightweight aluminum')
        if (material === 'Galvanized Steel') features.push('Corrosion resistant')
        if (selectedModel.brakes !== 'None') features.push(`${selectedModel.brakes} brakes`)
        if (selectedModel.tongueJack !== 'Optional') features.push('Tongue jack included')
        if (selectedModel.rollers) features.push('Self-centering rollers')
        if (type === 'Bunk') features.push('Carpeted bunks')
        features.push('LED lights', 'Galvanized hardware')
        
        setFormData(prev => ({
          ...prev,
          trailer_model_name: value,
          model_name: selectedModel.model,
          category_id: categoryId.toString(),
          type: type,
          material: material,
          axle_configuration: axleConfig,
          capacity_pounds: capacityNum.toString(),
          length_feet: lengthNum.toString(),
          beam_width_inches: beamNum.toString(),
          boat_length: selectedModel.boatLength || '',
          description: `${selectedModel.model} - ${selectedModel.boatLength} capacity ${type.toLowerCase()} trailer with ${selectedModel.axles} axle(s)`,
          // Individual specification fields from CSV data
          tongue_weight: '',
          overall_length: selectedModel.overallLength.replace(/[^0-9.]/g, '') || '',
          tire_size: selectedModel.tireSize || '',
          hitch_size: selectedModel.couplerSize?.replace(/[^0-9/-]/g, '') || '2',
          brake_type: selectedModel.brakes || 'None',
          suspension: '',
          frame: material === 'Aluminum' ? 'Aluminum' : 'Galvanized Steel',
          hull_protection: type === 'Bunk' ? 'Carpeted bunks' : 'Roller system',
          guides: 'Load guides included',
          winch: 'Winch ready',
          jack: selectedModel.tongueJack || 'Optional',
          wiring: 'LED lighting system',
          lights: 'LED tail lights',
          fenders: material === 'Aluminum' ? 'Aluminum diamond plate' : 'Steel fenders',
          finish: material === 'Aluminum' ? 'Mill finish aluminum' : 'Hot-dip galvanized',
          selectedFeatures: features
        }))
        return
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleFeatureChange = (feature: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      selectedFeatures: checked
        ? [...(prev.selectedFeatures || []), feature]
        : (prev.selectedFeatures || []).filter(f => f !== feature)
    }))
  }

  const handleImageSelect = (file: File) => {
    setFormData(prev => ({
      ...prev,
      image_file: file.size > 0 ? file : null
    }))
  }

  const handleMultipleImagesSelect = (images: (File | string)[]) => {
    console.log('handleMultipleImagesSelect called with images:', images);
    console.log('Images length:', images.length);
    console.log('Previous all_images:', formData.all_images);

    setFormData(prev => ({
      ...prev,
      all_images: images
    }));

    console.log('Updated formData.all_images to:', images);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const editingItem = editingItemId ? inventory.find(item => item.id === editingItemId) : null
      let image_url = editingItem?.image_url || null
      let image_urls = editingItem?.image_urls || []

      // Handle multiple image uploads and reordering
      // Use the reordered images from formData.all_images instead of reconstructing
      const allImages: string[] = []

      if (formData.all_images && formData.all_images.length > 0) {
        // Process each image in the reordered array
        for (const image of formData.all_images) {
          if (typeof image === 'string') {
            // Existing image URL
            allImages.push(image)
          } else {
            // New file that needs to be uploaded
            try {
              const imageFormData = new FormData()
              imageFormData.append('image', image)
              imageFormData.append('category', 'trailers')

              const imageResponse = await fetch('/api/upload-image', {
                method: 'POST',
                body: imageFormData
              })

              if (imageResponse.ok) {
                const imageResult = await imageResponse.json()
                allImages.push(imageResult.url)
              } else {
                console.warn('Failed to upload image:', image.name)
              }
            } catch (error) {
              console.warn('Error uploading image:', error)
            }
          }
        }

        if (allImages.length > 0) {
          image_urls = allImages
          // Set the first image as the main image_url for backward compatibility
          if (!image_url) {
            image_url = allImages[0]
          }
        }
      }

      // Handle single image upload if a file was selected (backward compatibility)
      if (formData.image_file && formData.image_file.size > 0) {
        try {
          const imageFormData = new FormData()
          imageFormData.append('image', formData.image_file)
          imageFormData.append('category', 'trailers')

          console.log('Attempting image upload...')
          
          const imageResponse = await fetch('/api/upload-image', {
            method: 'POST',
            body: imageFormData
          }).catch(fetchError => {
            // Handle network errors or other fetch failures
            console.warn('Image upload network error:', fetchError)
            throw new Error('Network error during image upload')
          })

          if (!imageResponse.ok) {
            console.warn(`Image upload failed with status ${imageResponse.status}: ${imageResponse.statusText}`)
            image_url = null
          } else {
            try {
              const imageResult = await imageResponse.json()
              image_url = imageResult.url
              console.log('Image uploaded successfully:', image_url)
            } catch (jsonError) {
              console.warn('Error parsing image upload response:', jsonError)
              image_url = null
            }
          }
        } catch (uploadError) {
          console.warn('Image upload error:', uploadError)
          image_url = null
        }
      }

      // Build comprehensive specifications object from individual fields
      const specifications = {
        tongue_weight: formData.tongue_weight ? formData.tongue_weight + ' lbs' : '',
        overall_length: formData.overall_length ? formData.overall_length + ' ft' : '',
        tire_size: formData.tire_size || '',
        hitch_size: formData.hitch_size ? formData.hitch_size + ' inch' : '',
        brake_type: formData.brake_type || '',
        suspension: formData.suspension || '',
        frame: formData.frame || '',
        hull_protection: formData.hull_protection || '',
        guides: formData.guides || '',
        winch: formData.winch || '',
        jack: formData.jack || '',
        wiring: formData.wiring || '',
        lights: formData.lights || '',
        fenders: formData.fenders || '',
        finish: formData.finish || ''
      }

      // Validate and constrain numeric values to prevent database overflow
      const capacity = parseInt(formData.capacity_pounds) || 0
      const length = parseFloat(formData.length_feet) || 0
      const beam = parseFloat(formData.beam_width_inches) || 0
      const price = parseFloat(formData.price) || 0
      const stock = parseInt(formData.stock_quantity) || 0

      const payload = {
        model_name: formData.model_name,
        category_id: parseInt(formData.category_id),
        type: formData.type,
        material: formData.material,
        axle_configuration: formData.axle_configuration,
        capacity_pounds: Math.min(capacity, 2147483647), // Max 32-bit integer
        length_feet: Math.min(length, 999.9), // Max for database precision(4,1)
        beam_width_inches: Math.min(beam, 999.9), // Max for database precision(4,1)
        price: Math.min(price, 9999999.99), // Max price constraint
        stock_quantity: Math.min(stock, 32767), // Max smallint
        image_url: image_url,
        image_urls: image_urls,
        description: formData.description,
        boat_length: formData.boat_length || null,
        notes: formData.notes || null,
        features: formData.selectedFeatures,
        specifications: specifications
      }

      let response
      if (editingItemId) {
        // Update existing item
        response = await fetch(`/api/inventory/${editingItemId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        })
      } else {
        // Create new item
        response = await fetch('/api/inventory', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        })
      }

      if (!response.ok) {
        throw new Error(`Failed to save inventory item: ${response.statusText}`)
      }

      // Show success message, especially if image upload failed
      const hasImageFiles = (formData.image_files && formData.image_files.length > 0) || (formData.image_file && formData.image_file.size > 0)
      if (hasImageFiles && !image_url && image_urls.length === 0) {
        setError(`Item saved successfully, but image upload failed. You can edit the item later to add images.`)
        setTimeout(() => setError(null), 5000) // Clear message after 5 seconds
      }

      // Reset form and close
      resetForm()
      if (editingItemId) {
        setEditingItemId(null)
      } else {
        setShowAddForm(false)
      }
      
      // Refresh inventory
      await fetchInventoryData()
      
      // Dispatch custom event to notify other components (like InventoryPage) of the update
      console.log('Dispatching inventory-updated event after save')
      const event = new CustomEvent('inventory-updated', {
        detail: {
          action: editingItemId ? 'update' : 'create',
          itemId: editingItemId || 'new',
          timestamp: Date.now()
        }
      })
      window.dispatchEvent(event)
    } catch (err) {
      setError(`Failed to save inventory item: ${err instanceof Error ? err.message : 'Unknown error'}`)
      console.error('Error saving inventory:', err)
    }
  }

  const handleEdit = (item: TrailerInventoryItem) => {
    if (editingItemId === item.id) {
      // If already editing this item, close the edit form
      setEditingItemId(null)
      resetForm()
    } else {
      // Edit a new item
      setEditingItemId(item.id)
      setFormData({
        trailer_model_name: '',
        model_name: item.model_name,
        category_id: item.category_id.toString(),
        type: item.type,
        material: item.material,
        axle_configuration: item.axle_configuration,
        capacity_pounds: item.capacity_pounds.toString(),
        length_feet: item.length_feet.toString(),
        beam_width_inches: item.beam_width_inches.toString(),
        price: item.price.toString(),
        stock_quantity: item.stock_quantity.toString(),
        image_file: null,
        image_files: [],
        existing_image_urls: item.image_urls || [],
        all_images: item.image_urls || [],
        description: item.description,
        boat_length: item.boat_length || '',
        notes: item.notes || '',
        // Individual specification fields
        tongue_weight: item.specifications.tongue_weight?.replace(' lbs', '') || '',
        overall_length: item.specifications.overall_length?.replace(' ft', '') || '',
        tire_size: item.specifications.tire_size || '',
        hitch_size: item.specifications.hitch_size?.replace(' inch', '') || '',
        brake_type: item.specifications.brake_type || '',
        suspension: item.specifications.suspension || '',
        frame: item.specifications.frame || '',
        hull_protection: item.specifications.hull_protection || '',
        guides: item.specifications.guides || '',
        winch: item.specifications.winch || '',
        jack: item.specifications.jack || '',
        wiring: item.specifications.wiring || '',
        lights: item.specifications.lights || '',
        fenders: item.specifications.fenders || '',
        finish: item.specifications.finish || '',
        // Features as array
        selectedFeatures: Array.isArray(item.features) ? item.features : []
      })
    }
  }

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this inventory item?')) {
      try {
        const response = await fetch(`/api/inventory/${id}`, {
          method: 'DELETE'
        })

        if (!response.ok) {
          throw new Error(`Failed to delete inventory item: ${response.statusText}`)
        }

        // Refresh inventory to reflect changes
        await fetchInventoryData()
        
        // Dispatch custom event to notify other components of the deletion
        console.log('Dispatching inventory-updated event after delete')
        const event = new CustomEvent('inventory-updated', {
          detail: {
            action: 'delete',
            itemId: id,
            timestamp: Date.now()
          }
        })
        window.dispatchEvent(event)
      } catch (err) {
        setError(`Failed to delete inventory item: ${err instanceof Error ? err.message : 'Unknown error'}`)
        console.error('Error deleting inventory:', err)
      }
    }
  }

  const handleStockUpdate = async (id: number, newQuantity: number) => {
    try {
      // Find the current item to get all its data
      const currentItem = inventory.find(item => item.id === id)
      if (!currentItem) return

      const response = await fetch(`/api/inventory/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...currentItem,
          stock_quantity: newQuantity
        })
      })

      if (!response.ok) {
        throw new Error(`Failed to update stock quantity: ${response.statusText}`)
      }

      // Update local state immediately for better UX
      setInventory(prev => prev.map(item =>
        item.id === id ? { ...item, stock_quantity: newQuantity } : item
      ))
      
      // Dispatch custom event to notify other components of the stock update
      console.log('Dispatching inventory-updated event after stock update')
      const event = new CustomEvent('inventory-updated', {
        detail: {
          action: 'stock_update',
          itemId: id,
          newQuantity: newQuantity,
          timestamp: Date.now()
        }
      })
      window.dispatchEvent(event)
    } catch (err) {
      setError(`Failed to update stock quantity: ${err instanceof Error ? err.message : 'Unknown error'}`)
      console.error('Error updating stock:', err)
    }
  }

  const resetForm = () => {
    setFormData({
      trailer_model_name: '',
      model_name: '',
      category_id: '',
      type: '',
      material: '',
      axle_configuration: '',
      capacity_pounds: '',
      length_feet: '',
      beam_width_inches: '',
      price: '',
      stock_quantity: '',
      image_file: null,
      image_files: [],
      existing_image_urls: [],
      all_images: [],
      description: '',
      boat_length: '',
      notes: '',
      // Individual specification fields
      tongue_weight: '',
      overall_length: '',
      tire_size: '',
      hitch_size: '',
      brake_type: '',
      suspension: '',
      frame: '',
      hull_protection: '',
      guides: '',
      winch: '',
      jack: '',
      wiring: '',
      lights: '',
      fenders: '',
      finish: '',
      // Features as checkboxes
      selectedFeatures: [] as string[]
    })
  }

  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.model_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === 'all' || item.category_id.toString() === selectedCategory
    return matchesSearch && matchesCategory
  })

  if (loading) {
    return (
      <div className="inventory-management">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading inventory management...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="inventory-management">
      <div className="inventory-header">
        <h2>Inventory Management</h2>
        <div className="header-actions">
          <button className="add-btn" onClick={() => { setShowAddForm(true); resetForm() }}>
            + Add New Trailer
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="inventory-filters">
        <input
          type="text"
          placeholder="Search inventory..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="category-select"
        >
          <option value="all">All Categories</option>
          {categories.map(category => (
            <option key={category.id} value={category.id.toString()}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      {showAddForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add New Inventory Item</h3>
              <button 
                className="modal-close"
                onClick={() => { setShowAddForm(false); resetForm() }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="inventory-form">
              <div className="form-group">
                <label>Trailer Model</label>
                <input
                  type="text"
                  placeholder="Search trailer models..."
                  value={modelSearchTerm}
                  onChange={(e) => setModelSearchTerm(e.target.value)}
                  className="model-search-input"
                />
                <select
                  name="trailer_model_name"
                  value={formData.trailer_model_name}
                  onChange={handleInputChange}
                  className="model-select"
                >
                  <option value="">Select Trailer Model - {filteredTrailerModels.length} models available</option>
                  {filteredTrailerModels.map(model => (
                    <option key={model.model} value={model.model}>
                      {model.model} - {model.sourceSheet} | {model.capacity} lbs | {model.boatLength} | {model.overallLength} | {model.axles === 1 ? 'Single' : model.axles === 2 ? 'Tandem' : 'Triple'} Axle
                    </option>
                  ))}
                </select>
                {formData.trailer_model_name && (
                  <div className="model-info-detailed">
                    <p className="model-selected">
                      ✓ Selected: <strong>{formData.trailer_model_name}</strong>
                    </p>
                    {(() => {
                      const selectedModel = trailerModels.find(m => m.model === formData.trailer_model_name)
                      if (selectedModel) {
                        return (
                          <div className="model-details">
                            <span className="detail-item">Type: {selectedModel.sourceSheet}</span>
                            <span className="detail-item">Capacity: {selectedModel.capacity} lbs</span>
                            <span className="detail-item">Boat Length: {selectedModel.boatLength}</span>
                            <span className="detail-item">Overall: {selectedModel.overallLength}</span>
                            <span className="detail-item">GVWR: {selectedModel.gvwr} lbs</span>
                            <span className="detail-item">Axles: {selectedModel.axles === 1 ? 'Single' : selectedModel.axles === 2 ? 'Tandem' : 'Triple'}</span>
                            <span className="detail-item">Brakes: {selectedModel.brakes}</span>
                            <span className="detail-item">Tires: {selectedModel.tireSize}</span>
                            <span className="detail-item">Jack: {selectedModel.tongueJack}</span>
                            {selectedModel.bowEyeMin && <span className="detail-item">Bow Eye: {selectedModel.bowEyeMin} - {selectedModel.bowEyeMax}</span>}
                            {selectedModel.betweenFenders && <span className="detail-item">Between Fenders: {selectedModel.betweenFenders}</span>}
                            {selectedModel.rollers && <span className="detail-item">Rollers: {selectedModel.rollers}</span>}
                          </div>
                        )
                      }
                      return null
                    })()}
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Model Name *</label>
                  <input
                    type="text"
                    name="model_name"
                    value={formData.model_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Type *</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="Bunk">Bunk</option>
                    <option value="Roller">Roller</option>
                    <option value="PWC">PWC</option>
                    <option value="Pontoon">Pontoon</option>
                    <option value="Tritoon">Tritoon</option>
                    <option value="Skiff">Skiff</option>
                    <option value="Jon Boat">Jon Boat</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Material *</label>
                  <select
                    name="material"
                    value={formData.material}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Material</option>
                    <option value="Aluminum">Aluminum</option>
                    <option value="Galvanized Steel">Galvanized Steel</option>
                    <option value="Steel">Steel</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Capacity (lbs) *</label>
                  <input
                    type="number"
                    name="capacity_pounds"
                    value={formData.capacity_pounds}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Length (ft) *</label>
                  <input
                    type="number"
                    step="0.1"
                    max="999.9"
                    name="length_feet"
                    value={formData.length_feet}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Beam Width (in) *</label>
                  <input
                    type="number"
                    step="0.1"
                    max="999.9"
                    name="beam_width_inches"
                    value={formData.beam_width_inches}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Price ($) *</label>
                  <input
                    type="number"
                    step="0.01"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Stock Quantity *</label>
                  <input
                    type="number"
                    name="stock_quantity"
                    value={formData.stock_quantity}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  {/* Empty for balance */}
                </div>
              </div>


              <div className="form-group">
                <MultipleImageUpload
                  onImagesSelect={handleMultipleImagesSelect}
                  currentImages={formData.all_images}
                  maxImages={10}
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Boat Length</label>
                  <input
                    type="text"
                    name="boat_length"
                    value={formData.boat_length}
                    onChange={handleInputChange}
                    placeholder="e.g., 16'-18'"
                  />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <input
                    type="text"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Additional notes"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Specifications</label>
                <div className="spec-grid">
                  <div className="spec-field">
                    <label>Axle Configuration</label>
                    <select
                      name="axle_configuration"
                      value={formData.axle_configuration}
                      onChange={handleInputChange}
                    >
                      <option value="">Select</option>
                      <option value="Single">Single</option>
                      <option value="Tandem">Tandem</option>
                      <option value="Triple">Triple</option>
                    </select>
                  </div>
                  <div className="spec-field">
                    <label>Tongue Weight (lbs)</label>
                    <input
                      type="number"
                      name="tongue_weight"
                      value={formData.tongue_weight}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="spec-field">
                    <label>Overall Length (ft)</label>
                    <input
                      type="number"
                      step="0.1"
                      max="999.9"
                      name="overall_length"
                      value={formData.overall_length}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="spec-field">
                    <label>Tire Size</label>
                    <input
                      type="text"
                      name="tire_size"
                      value={formData.tire_size}
                      onChange={handleInputChange}
                      placeholder="205/75R15"
                    />
                  </div>
                  <div className="spec-field">
                    <label>Hitch Size (inch)</label>
                    <select
                      name="hitch_size"
                      value={formData.hitch_size}
                      onChange={handleInputChange}
                    >
                      <option value="">Select</option>
                      <option value="1-7/8">1-7/8</option>
                      <option value="2">2</option>
                      <option value="2-5/16">2-5/16</option>
                    </select>
                  </div>
                  <div className="spec-field">
                    <label>Brake Type</label>
                    <select
                      name="brake_type"
                      value={formData.brake_type}
                      onChange={handleInputChange}
                    >
                      <option value="">Select</option>
                      <option value="None">None</option>
                      <option value="Surge">Surge</option>
                      <option value="Electric">Electric</option>
                      <option value="Electric over Hydraulic">Electric over Hydraulic</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Features</label>
                <div className="features-grid">
                  {availableFeatures.map(feature => (
                    <label key={feature} className="feature-checkbox">
                      <input
                        type="checkbox"
                        checked={(formData.selectedFeatures || []).includes(feature)}
                        onChange={(e) => handleFeatureChange(feature, e.target.checked)}
                      />
                      <span>{feature}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => { setShowAddForm(false); resetForm() }}>
                  Cancel
                </button>
                <button type="submit" className="primary">
                  Add Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="inventory-list">
        <div className="inventory-summary">
          {filteredInventory.length} item{filteredInventory.length !== 1 ? 's' : ''} found
        </div>
        
        <div className="inventory-table">
          <div className="table-header">
            <div className="col-image">Image</div>
            <div className="col-name">Model Name</div>
            <div className="col-category">Category</div>
            <div className="col-material">Material</div>
            <div className="col-price">Price</div>
            <div className="col-stock">Stock</div>
            <div className="col-actions">Actions</div>
          </div>
          
          {filteredInventory.map(item => (
            <div key={item.id}>
              <div className="table-row">
                <div className="col-image">
                  <img
                    src={item.image_url || '/images/placeholders/trailer-placeholder.svg'}
                    alt={`${item.model_name} ${item.type} trailer - ${item.material} ${item.axle_configuration} axle boat trailer for sale`}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/images/placeholders/trailer-placeholder.svg';
                    }}
                  />
                </div>
                <div className="col-name">
                  <strong>{item.model_name}</strong>
                  <p>{item.type} - {item.axle_configuration} Axle</p>
                </div>
                <div className="col-category">{item.category_name}</div>
                <div className="col-material">{item.material}</div>
                <div className="col-price">${item.price.toLocaleString()}</div>
                <div className="col-stock">
                  <input
                    type="number"
                    value={item.stock_quantity}
                    onChange={(e) => handleStockUpdate(item.id, parseInt(e.target.value) || 0)}
                    className="stock-input"
                  />
                </div>
                <div className="col-actions">
                  <button 
                    onClick={() => handleEdit(item)} 
                    className={`edit-btn ${editingItemId === item.id ? 'active' : ''}`}
                  >
                    {editingItemId === item.id ? 'Cancel' : 'Edit'}
                  </button>
                  <button onClick={() => handleDelete(item.id)} className="delete-btn">Delete</button>
                </div>
              </div>
              
              {editingItemId === item.id && (
                <div className="inline-edit-form">
                  <form onSubmit={handleSubmit} className="inventory-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Model Name *</label>
                        <input
                          type="text"
                          name="model_name"
                          value={formData.model_name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Category *</label>
                        <select
                          name="category_id"
                          value={formData.category_id}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select Category</option>
                          {categories.map(category => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Type *</label>
                        <select
                          name="type"
                          value={formData.type}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select Type</option>
                          <option value="Bunk">Bunk</option>
                          <option value="Roller">Roller</option>
                          <option value="PWC">PWC</option>
                          <option value="Pontoon">Pontoon</option>
                          <option value="Tritoon">Tritoon</option>
                          <option value="Skiff">Skiff</option>
                          <option value="Jon Boat">Jon Boat</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Material *</label>
                        <select
                          name="material"
                          value={formData.material}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select Material</option>
                          <option value="Aluminum">Aluminum</option>
                          <option value="Galvanized Steel">Galvanized Steel</option>
                          <option value="Steel">Steel</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Capacity (lbs) *</label>
                        <input
                          type="number"
                          name="capacity_pounds"
                          value={formData.capacity_pounds}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Length (ft) *</label>
                        <input
                          type="number"
                          step="0.1"
                          max="999.9"
                          name="length_feet"
                          value={formData.length_feet}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Beam Width (in) *</label>
                        <input
                          type="number"
                          step="0.1"
                          max="999.9"
                          name="beam_width_inches"
                          value={formData.beam_width_inches}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Price ($) *</label>
                        <input
                          type="number"
                          step="0.01"
                          name="price"
                          value={formData.price}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Stock Quantity *</label>
                        <input
                          type="number"
                          name="stock_quantity"
                          value={formData.stock_quantity}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        {/* Empty for balance */}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Length (ft) *</label>
                        <input
                          type="number"
                          step="0.1"
                          max="999.9"
                          name="length_feet"
                          value={formData.length_feet}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Beam Width (in) *</label>
                        <input
                          type="number"
                          step="0.1"
                          max="999.9"
                          name="beam_width_inches"
                          value={formData.beam_width_inches}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Price ($) *</label>
                        <input
                          type="number"
                          step="0.01"
                          name="price"
                          value={formData.price}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Stock Quantity *</label>
                        <input
                          type="number"
                          name="stock_quantity"
                          value={formData.stock_quantity}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <MultipleImageUpload
                        onImagesSelect={handleMultipleImagesSelect}
                        currentImages={formData.all_images}
                        maxImages={10}
                      />
                    </div>

                    <div className="form-group">
                      <label>Description *</label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={3}
                        required
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Boat Length</label>
                        <input
                          type="text"
                          name="boat_length"
                          value={formData.boat_length}
                          onChange={handleInputChange}
                          placeholder="e.g., 16'-18'"
                        />
                      </div>
                      <div className="form-group">
                        <label>Notes</label>
                        <input
                          type="text"
                          name="notes"
                          value={formData.notes}
                          onChange={handleInputChange}
                          placeholder="Additional notes"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Specifications</label>
                      <div className="spec-grid">
                        <div className="spec-field">
                          <label>Axle Configuration</label>
                          <select
                            name="axle_configuration"
                            value={formData.axle_configuration}
                            onChange={handleInputChange}
                          >
                            <option value="">Select</option>
                            <option value="Single">Single</option>
                            <option value="Tandem">Tandem</option>
                            <option value="Triple">Triple</option>
                          </select>
                        </div>
                        <div className="spec-field">
                          <label>Tongue Weight (lbs)</label>
                          <input
                            type="number"
                            name="tongue_weight"
                            value={formData.tongue_weight}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="spec-field">
                          <label>Overall Length (ft)</label>
                          <input
                            type="number"
                            step="0.1"
                            max="999.9"
                            name="overall_length"
                            value={formData.overall_length}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="spec-field">
                          <label>Tire Size</label>
                          <input
                            type="text"
                            name="tire_size"
                            value={formData.tire_size}
                            onChange={handleInputChange}
                            placeholder="205/75R15"
                          />
                        </div>
                        <div className="spec-field">
                          <label>Hitch Size (inch)</label>
                          <select
                            name="hitch_size"
                            value={formData.hitch_size}
                            onChange={handleInputChange}
                          >
                            <option value="">Select</option>
                            <option value="1-7/8">1-7/8</option>
                            <option value="2">2</option>
                            <option value="2-5/16">2-5/16</option>
                          </select>
                        </div>
                        <div className="spec-field">
                          <label>Brake Type</label>
                          <select
                            name="brake_type"
                            value={formData.brake_type}
                            onChange={handleInputChange}
                          >
                            <option value="">Select</option>
                            <option value="None">None</option>
                            <option value="Surge">Surge</option>
                            <option value="Electric">Electric</option>
                            <option value="Electric over Hydraulic">Electric over Hydraulic</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Features</label>
                      <div className="features-grid">
                        {availableFeatures.map(feature => (
                          <label key={feature} className="feature-checkbox">
                            <input
                              type="checkbox"
                              checked={(formData.selectedFeatures || []).includes(feature)}
                              onChange={(e) => handleFeatureChange(feature, e.target.checked)}
                            />
                            <span>{feature}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="form-actions">
                      <button type="button" onClick={() => { setEditingItemId(null); resetForm() }}>
                        Cancel
                      </button>
                      <button type="submit" className="primary">
                        Update Item
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default InventoryManagement